import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiCheckCircle,
  FiRefreshCw,
  FiClock,
  FiXCircle,
  FiUpload,
  FiEye,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import {
  APPLICANTS_QUERY,
  BACKGROUND_CHECKS_QUERY,
  CREATE_BACKGROUND_CHECK,
  UPDATE_BACKGROUND_STATUS,
  UPDATE_BACKGROUND_CHECK,
  BUMP_BACKGROUND_DOCS,
  applicantInitials,
  mapApplicant,
} from '../data/recruitmentApi';
import '../styles/recruitmentShared.css';
import '../styles/AddApplicant.css';
import '../styles/BackgroundChecks.css';

const CHECK_LABEL = {
  verified: 'Verified',
  in_progress: 'In Progress',
  pending: 'Pending',
  rejected: 'Rejected',
};

const CHECK_ICON = {
  verified: FiCheckCircle,
  in_progress: FiRefreshCw,
  pending: FiClock,
  rejected: FiXCircle,
};

const CHECK_CYCLE = ['pending', 'in_progress', 'verified', 'rejected'];

const CHECK_FIELDS = [
  { key: 'policeCheck', label: 'Police Check' },
  { key: 'identityCheck', label: 'Identity Verification' },
  { key: 'referenceCheck', label: 'Reference Check' },
  { key: 'employmentCheck', label: 'Employment Verification' },
];

function nextCheckStatus(current) {
  const idx = CHECK_CYCLE.indexOf(current);
  return CHECK_CYCLE[(idx + 1) % CHECK_CYCLE.length];
}

export default function BackgroundChecks() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const overlay = useAdminListOverlay(rows);
  // Overlay: action=add create, view=<id> review.
  const active = overlay.viewing;
  const mode = overlay.showAdd ? 'create' : overlay.viewing ? 'review' : null;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    applicantId: '',
    name: '',
    role: '',
  });
  const [reviewForm, setReviewForm] = useState({
    policeCheck: 'pending',
    identityCheck: 'pending',
    referenceCheck: 'pending',
    employmentCheck: 'pending',
    notes: '',
    status: 'pending',
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [bgData, applicantData] = await Promise.all([
        graphqlRequest(BACKGROUND_CHECKS_QUERY),
        graphqlRequest(APPLICANTS_QUERY),
      ]);
      setRows(
        Array.isArray(bgData?.recruitmentBackgroundChecks)
          ? bgData.recruitmentBackgroundChecks
          : [],
      );
      setApplicants(
        Array.isArray(applicantData?.recruitmentApplicants)
          ? applicantData.recruitmentApplicants.map(mapApplicant)
          : [],
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to load background checks.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const stats = useMemo(
    () => [
      {
        id: 'verified',
        label: 'Verified',
        value: rows.filter((r) => r.status === 'verified').length,
        icon: FiCheckCircle,
        tone: 'green',
      },
      {
        id: 'in_progress',
        label: 'In Progress',
        value: rows.filter((r) => r.status === 'in_progress').length,
        icon: FiRefreshCw,
        tone: 'yellow',
      },
      {
        id: 'pending',
        label: 'Pending',
        value: rows.filter((r) => r.status === 'pending').length,
        icon: FiClock,
        tone: 'gray',
      },
      {
        id: 'rejected',
        label: 'Rejected',
        value: rows.filter((r) => r.status === 'rejected').length,
        icon: FiXCircle,
        tone: 'red',
      },
    ],
    [rows],
  );

  const openCreate = () => {
    setForm({ applicantId: '', name: '', role: '' });
    overlay.openAdd();
  };

  const openReview = (row) => overlay.openView(row);

  useEffect(() => {
    const row = overlay.viewing;
    if (!row) return;
    setReviewForm({
      policeCheck: row.policeCheck || 'pending',
      identityCheck: row.identityCheck || 'pending',
      referenceCheck: row.referenceCheck || 'pending',
      employmentCheck: row.employmentCheck || 'pending',
      notes: row.notes || '',
      status: row.status || 'pending',
    });
  }, [overlay.viewing]);

  const closeModal = () => overlay.closeOverlay();

  const onPickApplicant = (id) => {
    const a = applicants.find((x) => x.id === id);
    setForm({
      applicantId: id,
      name: a?.name || '',
      role: a?.role || '',
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      toast.error('Name and role are required.');
      return;
    }
    setSaving(true);
    try {
      await graphqlRequest(CREATE_BACKGROUND_CHECK, {
        input: {
          applicantId: form.applicantId || null,
          name: form.name.trim(),
          role: form.role.trim(),
        },
      });
      toast.success('Candidate added to background checks.');
      closeModal();
      await fetchRows();
    } catch (err) {
      toast.error(err?.message || 'Could not add candidate.');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await graphqlRequest(UPDATE_BACKGROUND_STATUS, { id, status });
      toast.success(
        status === 'verified'
          ? 'Approved. Candidate moved to Onboarding.'
          : 'Status updated.',
      );
      await fetchRows();
    } catch (err) {
      toast.error(err?.message || 'Could not update status.');
    }
  };

  const uploadDoc = async (id) => {
    try {
      await graphqlRequest(BUMP_BACKGROUND_DOCS, { id });
      toast.success('Document marked as uploaded.');
      await fetchRows();
    } catch (err) {
      toast.error(err?.message || 'Could not upload document.');
    }
  };

  const cycleCheck = async (row, field) => {
    const next = nextCheckStatus(row[field] || 'pending');
    try {
      await graphqlRequest(UPDATE_BACKGROUND_CHECK, {
        id: row.id,
        input: { [field]: next },
      });
      toast.success(`${field.replace('Check', '')} → ${CHECK_LABEL[next]}`);
      await fetchRows();
    } catch (err) {
      toast.error(err?.message || 'Could not update check.');
    }
  };

  const saveReview = async (e) => {
    e.preventDefault();
    if (!active?.id) return;
    setSaving(true);
    try {
      await graphqlRequest(UPDATE_BACKGROUND_CHECK, {
        id: active.id,
        input: {
          policeCheck: reviewForm.policeCheck,
          identityCheck: reviewForm.identityCheck,
          referenceCheck: reviewForm.referenceCheck,
          employmentCheck: reviewForm.employmentCheck,
          notes: reviewForm.notes.trim() || null,
          status: reviewForm.status,
        },
      });
      toast.success('Background check updated.');
      closeModal();
      await fetchRows();
    } catch (err) {
      toast.error(err?.message || 'Could not save review.');
    } finally {
      setSaving(false);
    }
  };

  const renderCheck = (row, field, label) => {
    const status = row[field] || 'pending';
    const Icon = CHECK_ICON[status] || FiClock;
    return (
      <button
        key={field}
        type="button"
        className={`bc-check bc-check--${status} bc-check--clickable`}
        title="Click to update status"
        onClick={() => void cycleCheck(row, field)}
      >
        <Icon />
        <div>
          <strong>{label}</strong>
          <span>{CHECK_LABEL[status] || status}</span>
        </div>
      </button>
    );
  };

  return (
    <div className="rm-shared bc-page">
      <nav className="rm-crumb" aria-label="Breadcrumb">
        <button
          type="button"
          className="rm-crumb-back"
          onClick={() =>
            navigate({ pathname: '/', search: '?page=recruitment' })
          }
        >
          Recruitment
        </button>
        <span className="rm-crumb-sep">/</span>
        <span>Background Checks</span>
      </nav>

      <header className="rm-page-header">
        <div>
          <h1 className="rm-page-title">Background Verification</h1>
          <p className="rm-page-subtitle">
            Track identity, criminal, and employment verification for all
            candidates. Click a check tile to update its status.
          </p>
        </div>
        <button
          type="button"
          className="rm-btn rm-btn--primary"
          onClick={openCreate}
        >
          <FiPlus />
          Add Candidate
        </button>
      </header>

      <section className="bc-stats">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <article key={s.id} className="rm-stat-card">
              <div
                className={`rm-stat-icon rm-stat-icon--${
                  s.tone === 'gray' ? 'yellow' : s.tone
                }`}
              >
                <Icon />
              </div>
              <div>
                <div className="rm-stat-value">{s.value}</div>
                <div className="rm-stat-label">{s.label}</div>
              </div>
            </article>
          );
        })}
      </section>

      {loading ? (
        <div className="bc-empty">Loading background checks…</div>
      ) : rows.length === 0 ? (
        <div className="bc-empty">
          No background checks yet. Click Add Candidate to start.
        </div>
      ) : (
        <div className="bc-grid">
          {rows.map((row) => (
            <article key={row.id} className="bc-card">
              <div className="bc-card-top">
                <div className="rm-applicant-cell">
                  <div
                    className="rm-avatar"
                    style={{ background: row.avatarColor || '#60a5fa' }}
                  >
                    {applicantInitials(row.name)}
                  </div>
                  <div>
                    <p className="rm-applicant-name">{row.name}</p>
                    <p className="rm-applicant-id">
                      {row.role} · {row.bgcId}
                    </p>
                  </div>
                </div>
                <div className="bc-top-right">
                  <span className={`bc-status bc-status--${row.status}`}>
                    {CHECK_LABEL[row.status] || row.status}
                  </span>
                  <span className="bc-docs">
                    {row.docsUploaded || 0} docs uploaded
                  </span>
                </div>
              </div>

              <div className="bc-checks">
                {CHECK_FIELDS.map((c) => renderCheck(row, c.key, c.label))}
              </div>

              <div className="bc-actions">
                <button
                  type="button"
                  className="bc-action"
                  onClick={() => void uploadDoc(row.id)}
                >
                  <FiUpload /> Upload
                </button>
                <button
                  type="button"
                  className="bc-action"
                  onClick={() => openReview(row)}
                >
                  <FiEye /> Review
                </button>
                <button
                  type="button"
                  className="bc-action bc-action--ok"
                  onClick={() => void setStatus(row.id, 'verified')}
                >
                  <FiCheck /> Approve
                </button>
                <button
                  type="button"
                  className="bc-action bc-action--danger"
                  onClick={() => void setStatus(row.id, 'rejected')}
                >
                  <FiX /> Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {mode === 'create' ? (
        <div className="aa-overlay" onClick={closeModal}>
          <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aa-header">
              <div>
                <h2>Add Candidate</h2>
                <p>Start background verification for a candidate.</p>
              </div>
              <button type="button" className="aa-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form className="aa-form" onSubmit={handleCreate}>
              <label className="aa-field aa-field--full">
                <span>Applicant</span>
                <select
                  value={form.applicantId}
                  onChange={(e) => onPickApplicant(e.target.value)}
                >
                  <option value="">Select applicant (optional)</option>
                  {applicants.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} — {a.role}
                    </option>
                  ))}
                </select>
              </label>
              <div className="aa-grid">
                <label className="aa-field">
                  <span>Candidate name *</span>
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="aa-field">
                  <span>Job role *</span>
                  <input
                    value={form.role}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, role: e.target.value }))
                    }
                    required
                  />
                </label>
              </div>
              <div className="aa-actions">
                <button
                  type="button"
                  className="aa-btn aa-btn--ghost"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aa-btn aa-btn--primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Add Candidate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {mode === 'review' && active ? (
        <div className="aa-overlay" onClick={closeModal}>
          <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aa-header">
              <div>
                <h2>Review checks</h2>
                <p>
                  {active.name} · {active.role} · {active.bgcId}
                </p>
              </div>
              <button type="button" className="aa-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form className="aa-form" onSubmit={saveReview}>
              <div className="aa-grid">
                {CHECK_FIELDS.map((c) => (
                  <label key={c.key} className="aa-field">
                    <span>{c.label}</span>
                    <select
                      value={reviewForm[c.key]}
                      onChange={(e) =>
                        setReviewForm((p) => ({
                          ...p,
                          [c.key]: e.target.value,
                        }))
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="verified">Verified</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </label>
                ))}
                <label className="aa-field">
                  <span>Overall status</span>
                  <select
                    value={reviewForm.status}
                    onChange={(e) =>
                      setReviewForm((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </label>
              </div>
              <label className="aa-field aa-field--full">
                <span>Notes</span>
                <textarea
                  rows={3}
                  value={reviewForm.notes}
                  onChange={(e) =>
                    setReviewForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </label>
              <div className="aa-actions">
                <button
                  type="button"
                  className="aa-btn aa-btn--ghost"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aa-btn aa-btn--primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
